import React from 'react';

import i18n from '@client/utils/i18n';
import { Close } from '@mui/icons-material';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';

export interface CloudBackupEntry {
    filename: string;
    version: string;
    date: string;
}

interface CloudRestoreDialogProps {
    isOpen: boolean;
    onClose: () => void;
    loading: boolean;
    backups: CloudBackupEntry[];
    currentVersion: string | null;
    onRestore: (backup: CloudBackupEntry) => void;
}

const CloudRestoreDialog = ({ isOpen, onClose, loading, backups, currentVersion, onRestore }: CloudRestoreDialogProps) => {
    const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {i18n('cloud_restore')}
                <IconButton edge="end" onClick={onClose} size="small" aria-label="close">
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, overflowY: 'auto', maxHeight: '60vh' }}>
                {loading && (
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={4} sx={{ textAlign: 'center', border: 0 }}>
                                    <CircularProgress size={32} />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                )}
                {!loading && backups.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, px: 3 }}>
                        {i18n('cloud_restore_no_backups')}
                    </Typography>
                )}
                {!loading && backups.length > 0 && (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{i18n('cloud_restore_col_version')}</TableCell>
                                <TableCell>{i18n('cloud_restore_col_date')}</TableCell>
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {backups.map((backup) => (
                                <TableRow
                                    key={backup.filename}
                                    onMouseEnter={() => setHoveredRow(backup.filename)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                    sx={{ '&:last-child td': { border: 0 } }}
                                >
                                    <TableCell>{backup.version}</TableCell>
                                    <TableCell>{backup.date}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {currentVersion && backup.version !== currentVersion && (
                                            <Typography variant="caption" color="error">
                                                {i18n('cloud_restore_version_mismatch')}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right', width: '160px', whiteSpace: 'nowrap', py: '6px' }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="primary"
                                            sx={{ visibility: hoveredRow === backup.filename ? 'visible' : 'hidden' }}
                                            onClick={() => onRestore(backup)}
                                        >
                                            {i18n('cloud_restore_cta')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CloudRestoreDialog;
